import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { InitialPage } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/InitialPage/InitialPage.tsx';
import { BasicDetails } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/BasicDetails.tsx';
import { PersonHistoryPage } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/History/PersonHistoryPage.tsx';
import { DocumentsLayout } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Documents/DocumentsLayout';
import { HoldingDetails as Holding } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingDetails/HoldingDetails.tsx';
import { Holdings } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/Holdings.tsx';
import { Ownership } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Ownership/Ownership.tsx';
import { Securities } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/Securities.tsx';
import { SecurityDetails as Security } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityDetails.tsx';
import { SecurityRanking } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityRanking/SecurityRanking.tsx';
import { TransactionDetails as Transaction } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/TransactionDetails/TransactionDetails.tsx';
import { Transactions } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Transactions/Transactions.tsx';
import { DEAL_ROUTES, PEOPLE_ROUTES, ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import { MantineTabs } from '@dealstack/frontend/shared/components/MantineTabs/MantineTabs.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { SentryRoutes } from '@dealstack/frontend/shared/sentry.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { useContext, useMemo } from 'react';
import { generatePath, Navigate, Route, useLocation, useNavigate } from 'react-router-dom';
import { MantineTabProps } from '@dealstack/frontend/shared/components/MantineTabs/MantineTab.types';

export const DealDirectoryContent = () => {
	const tCapManage = useTranslationCapManage();
	const {
		selectedEntityId: personId,
		dealId,
		selectedEntityData,
		isFetchingSelectedEntityData,
		dealCurrency,
		personsSelectOptions,
		mipSelectOptions,
	} = useContext(CapManageControllerContext);

	const navigate = useNavigate();
	const location = useLocation();

	const hasExistingSecurities = !!selectedEntityData?.hasSecurities;
	const canIssueSecurities = !!selectedEntityData?.canIssueSecurities;

	const tabsConfig = useMemo(
		(): MantineTabProps<PEOPLE_ROUTES>[] => [
			{
				label: tCapManage('tabs.basic_details.title'),
				value: PEOPLE_ROUTES.PERSON_DETAILS,
				icon: 'information',
			},
			{
				label: tCapManage('tabs.securities.title'),
				value: PEOPLE_ROUTES.SECURITIES,
				icon: 'pie',
				testId: TEST_ELEMENT_IDS.CAP_TAB_SECURITIES,
				disabled: !canIssueSecurities,
			},
			{
				label: tCapManage('tabs.transactions.title'),
				value: PEOPLE_ROUTES.TRANSACTIONS,
				icon: 'money-v3',
				testId: TEST_ELEMENT_IDS.CAP_TAB_TRANSACTIONS,
				disabled: !hasExistingSecurities && !canIssueSecurities,
			},
			{
				label: tCapManage('tabs.holdings.title'),
				value: PEOPLE_ROUTES.OWNERSHIP,
				icon: 'holding-pie',
				testId: TEST_ELEMENT_IDS.CAP_OWNERSHIP_TAB,
				disabled: !hasExistingSecurities && !canIssueSecurities,
			},
			{
				label: tCapManage('tabs.ownership.title'),
				value: PEOPLE_ROUTES.HOLDINGS,
				icon: 'derived_commuted_structure',
			},
			{
				label: tCapManage('tabs.documents.title'),
				value: PEOPLE_ROUTES.DOCUMENTS,
				icon: 'folder',
				disabled: !canIssueSecurities,
			},
		],
		[tCapManage, hasExistingSecurities, canIssueSecurities]
	);

	const tabValue = useMemo(
		() =>
			tabsConfig.find((tab) => location.pathname.includes(tab.value))?.value ??
			PEOPLE_ROUTES.PERSON_DETAILS,
		[location.pathname, tabsConfig]
	);

	if (!personId) {
		return (
			<SentryRoutes>
				<Route path="*" element={<InitialPage />} />
			</SentryRoutes>
		);
	}

	return (
		<MantineTabs
			tabs={tabsConfig}
			panels={[]}
			onlyDividerBorder={true}
			value={tabValue}
			onTabChange={(value) => {
				navigate(
					generatePath(`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}${value}`, {
						dealId: dealId ?? '',
						personId: personId ?? '',
					})
				);
			}}
		>
			<SentryRoutes>
				<Route path={PEOPLE_ROUTES.INITIAL} element={<InitialPage />} />
				<Route
					path={PEOPLE_ROUTES.PERSON_DETAILS}
					element={
						<BasicDetails
							selectedEntityData={selectedEntityData}
							selectedEntityLoading={isFetchingSelectedEntityData}
						/>
					}
				/>
				<Route
					path={PEOPLE_ROUTES.SECURITIES}
					element={
						<Securities
							selectedEntityLoading={isFetchingSelectedEntityData}
							dealCurrency={dealCurrency ?? undefined}
						/>
					}
				/>
				<Route
					path={PEOPLE_ROUTES.SECURITY_RANKING}
					element={
						<SecurityRanking selectedEntityLoading={isFetchingSelectedEntityData} />
					}
				/>
				<Route
					path={PEOPLE_ROUTES.SECURITY_DETAILS}
					element={<Security selectedEntityLoading={isFetchingSelectedEntityData} />}
				/>
				<Route
					path={PEOPLE_ROUTES.TRANSACTIONS}
					element={<Transactions selectedEntityLoading={isFetchingSelectedEntityData} />}
				/>
				<Route
					path={PEOPLE_ROUTES.TRANSACTION_DETAILS}
					element={
						<Transaction
							selectedEntityLoading={isFetchingSelectedEntityData}
							personSelectOptions={personsSelectOptions}
							mipSelectOptions={mipSelectOptions}
						/>
					}
				/>
				<Route path={PEOPLE_ROUTES.DOCUMENTS} element={<DocumentsLayout />} />
				<Route path={PEOPLE_ROUTES.PERSON_HISTORY} element={<PersonHistoryPage />} />
				<Route
					path={PEOPLE_ROUTES.OWNERSHIP}
					element={<Ownership selectedEntityLoading={isFetchingSelectedEntityData} />}
				/>
				<Route
					path={PEOPLE_ROUTES.HOLDINGS}
					element={<Holdings selectedEntityLoading={isFetchingSelectedEntityData} />}
				/>
				<Route
					path={PEOPLE_ROUTES.HOLDING_DETAILS}
					element={<Holding selectedEntityLoading={isFetchingSelectedEntityData} />}
				/>
				<Route
					path="*"
					element={
						<Navigate
							to={generatePath(
								`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.PERSON_DETAILS}`,
								{ dealId: dealId ?? '', personId }
							)}
						/>
					}
				/>
			</SentryRoutes>
		</MantineTabs>
	);
};
